/**
 * RejectedTab — «Scarti pertinenza» (iter182, Mirko 1/11): the rejected_articles ledger the travel-relevance gate
 * keeps out of the pool (generalist sources, travel_relevance=none — live gate, retroactive passes, manual ops).
 * Table (date, source, title+link, relevance, reason, origin), filters by source / period / origin, title search,
 * and a per-row «Ripesca» that re-fetches + re-summarises the article into the pool as industry_impact.
 * No permanent deletion from here.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, LifeBuoy, Search } from "lucide-react";
import { TabsContent } from "../ui/tabs";
import { API } from "../../config";

const ORIGINS = ["live", "pass1", "pass2", "cleanup", "manual"];
const PERIODS = [["24h", 1], ["7d", 7], ["30d", 30], ["all", 0]];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function RejectedTab({ dm, authHeader }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_origin: {} });
  const [source, setSource] = useState("");
  const [origin, setOrigin] = useState("");
  const [days, setDays] = useState(0);
  const [q, setQ] = useState("");
  const [qDraft, setQDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [rescuing, setRescuing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (source) params.source = source;
      if (origin) params.origin = origin;
      if (q) params.q = q;
      if (days) params.since = new Date(Date.now() - days * 86400000).toISOString();
      const { data } = await axios.get(`${API}/admin/rejected`, { params, headers: authHeader() });
      setRows(data.items || []); setTotal(data.total || 0); setPages(data.pages || 1); setSources(data.sources || []); setStats(data.stats || { total: 0, by_origin: {} });
    } catch (err) { toast.error(err.response?.data?.detail || "Impossibile caricare il ledger"); } finally { setLoading(false); }
  }, [authHeader, page, source, origin, q, days]);

  useEffect(() => { load(); }, [load]);

  const rescue = async (row) => {
    setRescuing(row.id);
    try {
      const { data } = await axios.post(`${API}/admin/rejected/${row.id}/rescue`, {}, { headers: authHeader() });
      toast.success(`Ripescato → articolo ${data.article_id.slice(0, 8)} (${data.refetched ? "pagina riletta" : "dati del ledger"}) · ${data.note}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id)); setTotal((t) => Math.max(0, t - 1));
      setStats((s) => ({ total: Math.max(0, (s.total || 0) - 1), by_origin: { ...s.by_origin, [row.origin]: Math.max(0, (s.by_origin?.[row.origin] || 0) - 1) } }));
    } catch (err) { toast.error(err.response?.data?.detail || "Ripesca fallito"); } finally { setRescuing(null); }
  };

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const pill = (active) => `px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${active ? (dm ? "bg-cyan-400/20 text-cyan-200" : "bg-[#006874]/15 text-[#006874]") : (dm ? "bg-[#0c1011] text-slate-300 hover:bg-white/5" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-black/5")}`;
  const chip = (color) => `px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold ${
    color === "amber" ? (dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-100 text-amber-700")
      : color === "rose" ? (dm ? "bg-rose-400/15 text-rose-300" : "bg-rose-100 text-rose-700")
        : color === "cyan" ? (dm ? "bg-cyan-400/15 text-cyan-300" : "bg-cyan-100 text-cyan-800")
          : (dm ? "bg-slate-700 text-slate-400" : "bg-slate-200 text-slate-600")}`;
  const originColor = (o) => (o === "live" ? "cyan" : o === "manual" ? "rose" : o === "pass1" || o === "pass2" ? "amber" : "slate");
  const select = `px-3 py-1.5 rounded-lg text-xs border-0 outline-none ${dm ? "bg-[#0c1011] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`;

  return (
    <TabsContent value="rejected">
      <div className="max-w-6xl space-y-6" data-testid="rejected-tab">
        <div>
          <h2 className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Scarti pertinenza</h2>
          <p className={`text-sm mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Articoli di fonti generaliste tenuti fuori dal pool dal gate travel &amp; tourism (RELEVANCE = none). «Ripesca» rilegge la pagina,
            rigenera il riassunto e salva l&apos;articolo come <code>industry_impact</code> con nota firmata; da allora i cleanup non lo toccano più. Niente cancellazioni definitive.
          </p>
        </div>

        <div className={card} data-testid="rejected-filters">
          <div className="flex items-center flex-wrap gap-2">
            <span className={`text-xs font-medium mr-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} data-testid="rejected-total">{stats.total} nel ledger</span>
            {ORIGINS.map((o) => (
              <button key={o} onClick={() => { setOrigin(origin === o ? "" : o); setPage(1); }} className={pill(origin === o)} data-testid={`rejected-origin-${o}`}>
                {o} <span className={chip(originColor(o))}>{stats.by_origin?.[o] || 0}</span>
              </button>
            ))}
            <span className={`mx-2 h-5 w-px ${dm ? "bg-white/10" : "bg-black/10"}`} />
            {PERIODS.map(([label, d]) => (
              <button key={label} onClick={() => { setDays(d); setPage(1); }} className={pill(days === d)} data-testid={`rejected-period-${label}`}>{label}</button>
            ))}
          </div>
          <div className="mt-3 flex items-center flex-wrap gap-2">
            <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} className={select} data-testid="rejected-source-select">
              <option value="">Tutte le fonti ({sources.length})</option>
              {sources.map((s) => <option key={s.source || "?"} value={s.source || ""}>{s.source || "?"} ({s.count})</option>)}
            </select>
            <form onSubmit={(e) => { e.preventDefault(); setQ(qDraft.trim()); setPage(1); }} className="flex items-center gap-1">
              <input value={qDraft} onChange={(e) => setQDraft(e.target.value)} placeholder="Cerca nel titolo…" className={`${select} w-64`} data-testid="rejected-search-input" />
              <button type="submit" className={pill(false)} data-testid="rejected-search-btn"><Search className="w-3.5 h-3.5" /></button>
            </form>
            <button onClick={() => load()} disabled={loading} className={`ml-auto p-2 rounded-lg ${dm ? "hover:bg-white/5 text-slate-400" : "hover:bg-black/5 text-[#6b7a7d]"}`} title="Aggiorna" data-testid="rejected-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        <div className={card} data-testid="rejected-table-card">
          {loading ? <div className="flex justify-center py-6"><RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>
            : rows.length === 0 ? <p className={`text-sm py-2 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} data-testid="rejected-empty">Nessuno scarto con questi filtri.</p>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="rejected-table">
                    <thead>
                      <tr className={`text-left text-[11px] uppercase tracking-wider ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                        <th className="py-2 pr-3 font-medium">Data</th><th className="py-2 pr-3 font-medium">Fonte</th><th className="py-2 pr-3 font-medium">Titolo</th>
                        <th className="py-2 pr-3 font-medium">Relevance</th><th className="py-2 pr-3 font-medium">Motivo</th><th className="py-2 pr-3 font-medium">Origine</th><th className="py-2 font-medium" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id} className={`border-t align-top ${dm ? "border-white/5" : "border-black/5"}`} data-testid={`rejected-row-${r.id}`}>
                          <td className={`py-2.5 pr-3 whitespace-nowrap text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>{fmt(r.rejected_at)}</td>
                          <td className={`py-2.5 pr-3 whitespace-nowrap text-xs ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>{r.source || "—"}</td>
                          <td className="py-2.5 pr-3 min-w-[260px] max-w-[460px]">
                            <a href={r.url} target="_blank" rel="noreferrer" className={`inline-flex items-start gap-1 hover:underline ${dm ? "text-slate-100" : "text-[#191c1d]"}`} data-testid={`rejected-title-${r.id}`}>
                              <span className="line-clamp-2">{r.title || r.url}</span><ExternalLink className="w-3 h-3 mt-1 flex-shrink-0 opacity-50" />
                            </a>
                          </td>
                          <td className="py-2.5 pr-3 whitespace-nowrap"><span className={chip("rose")}>{r.travel_relevance_was || "none"}</span></td>
                          <td className={`py-2.5 pr-3 text-xs max-w-[280px] ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} title={r.reason}><span className="line-clamp-2">{r.relevance_note || r.reason}</span></td>
                          <td className="py-2.5 pr-3 whitespace-nowrap"><span className={chip(originColor(r.origin))} data-testid={`rejected-origin-chip-${r.id}`}>{r.origin}</span></td>
                          <td className="py-2.5 whitespace-nowrap text-right">
                            <button onClick={() => rescue(r)} disabled={rescuing === r.id} title="Ripesca: torna nel pool come industry_impact (riletto e riassunto)"
                              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}
                              data-testid={`rejected-rescue-${r.id}`}>
                              {rescuing === r.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <LifeBuoy className="w-3 h-3" />} Ripesca
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          <div className={`mt-4 flex items-center justify-between text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            <span data-testid="rejected-count">{total} risultati · pagina {page}/{pages}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className={pill(false)} data-testid="rejected-prev">←</button>
              <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className={pill(false)} data-testid="rejected-next">→</button>
            </div>
          </div>
        </div>
      </div>
    </TabsContent>
  );
}
